// src/Aplicación/App.js
import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

// importar Google
import { GoogleOAuthProvider } from "@react-oauth/google";

// Importar Microsoft Authentication Library (MSAL)
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";

// Not found - 404
import NotFound from "../NotFound/NotFound"; // Agrega una página 404 si deseas

// Páginas principales
import HomePage from "../Homepage/HomePage";

import Menu from "../Menu/Menu";

// Catalogo
import Catalogo from "../Catalogo/Catalogo";

// Contextos
import { UserProvider } from "../Context/UserContext";
import { CompanyProvider } from "../Context/CompanyContext";

// Usuarios
import Login from "../Usuarios/Login";
import Register from "../Usuarios/Register";
import Profile from "../UserProfile/Profile";
import ForgetPassword from "../Usuarios/ForgetPassword";
import ChangePassword from "../Usuarios/ChangePassword";

// Crear Licitaciones
import CreateTenderStep1 from "../Licitaciones/CrearLicitacion/CreateTenderStep1";
import CreateTenderStep2 from "../Licitaciones/CrearLicitacion/CreateTenderStep2";
import CreateTenderStep3 from "../Licitaciones/CrearLicitacion/CreateTenderStep3";
import CreateTenderStep4 from "../Licitaciones/CrearLicitacion/CreateTenderStep4";
import PreviewTender from "../Licitaciones/CrearLicitacion/PreviewTender";
import SubirComprobante from "../Licitaciones/CrearLicitacion/SubirComprobante";

// Administrador de Licitaciones
import AdminLicitacion from "../Licitaciones/AdminLicitacion/AdminLicitacion";
import EditarLicitacion from "../Licitaciones/AdminLicitacion/Botones/EditarLicitacion";
import ReusarLicitacion from "../Licitaciones/AdminLicitacion/Botones/ReusarLicitacion";
import ConfirmarEdicion from "../Licitaciones/AdminLicitacion/Botones/ConfirmarEdicion";
import Postulaciones from "../Licitaciones/AdminLicitacion/Postulaciones/Postulaciones";
import PostulacionCompleta from "../Licitaciones/AdminLicitacion/Postulaciones/PostulacionCompleta";
import LicitacionEspecifica from "../Licitaciones/LicitacionEspecifica/LicitacionEspecifica";
import GanadorLicitacion from "../Licitaciones/AdminLicitacion/Postulaciones/GanadorLicitacion";
import AdminPostulaciones from "../Licitaciones/AdminPostulaciones/AdminPostulaciones";
import DetallePostulacion from "../Licitaciones/AdminPostulaciones/DetallePostulacion";

// Postulaciones
import Licitaciones from "../Licitaciones/BuscadorLicitaciones/Licitaciones";

// Perfil de empresa
import CompanyProfile from "../CompanyProfile/CompanyProfile";
import CompanyRegister from "../CompanyRegister/CompanyRegister";

import { Toaster } from "../components/ui/sonner";
import ChatWidget from "../ChatWidget/ChatWidget";

// Administración de empresa
import CompanyAdmin from "../CompanyAdmin/CompanyAdmin";
import CompanyUsers from "../CompanyAdmin/CompanyUsers";

// Componentes específicos de la administración de empresa
import Dashboard from "../CompanyAdmin/components/Dashboard";
import Identificacion from "../CompanyAdmin/components/Identificacion";
import Descripcion from "../CompanyAdmin/components/Descripcion";
import InformacionEmpresa from "../CompanyAdmin/components/InformacionEmpresa";
import Servicios from "../CompanyAdmin/components/Servicios";
import Contacto from "../CompanyAdmin/components/Contacto";
import Zonas from "../CompanyAdmin/components/Zonas";

// Importar el nuevo componente de Reseñas
import Reseñas from "../Reseñas/Reseñas";

// Legal
import CondicionesUso from "../Legal/CondicionesUso";
import AvisoPrivacidad from "../Legal/AvisoPrivacidad";

// Precios y ayuda
import Pricing from "../Pricing/Pricing";
import ComoFunciona from "../Ayuda/ComoFunciona";

// Invitación a licitación
import Invitacion from "../Invitacion/Invitacion";

// Métricas internas de Procu
import StaffMetrics from "../StaffMetrics/StaffMetrics";
import StaffCompanyAdmin from "../StaffMetrics/StaffCompanyAdmin";
import StaffChatDashboard from "../StaffMetrics/StaffChatDashboard";
import StaffPagosPendientes from "../StaffMetrics/StaffPagosPendientes";
import StaffVisitas from "../StaffMetrics/StaffVisitas";

// Analytics
import Analytics from "../Analytics/Analytics";

// Favoritos
import Favoritos from "../Favoritos/Favoritos";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
const MICROSOFT_AUTHORITY = process.env.REACT_APP_MICROSOFT_AUTHORITY;

// Configurar la instancia de Microsoft Authentication
const msalInstance = new PublicClientApplication({
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority:
      MICROSOFT_AUTHORITY || "https://login.microsoftonline.com/common",
    redirectUri: window.location.origin, // 🔹 Se ajusta dinámicamente
  },
});

const App = () => {
  return (
    <HelmetProvider>
    <Helmet>
      {/* Valores por defecto para Open Graph/Twitter; las páginas pueden sobreescribirlos con su propio <Helmet> */}
      <meta property="og:title" content="Procu - Conecta empresas y proveedores" />
      <meta
        property="og:description"
        content="Procu es la plataforma que conecta empresas con proveedores confiables. Publica licitaciones y encuentra socios estratégicos."
      />
      <meta property="og:image" content="https://procu.com.pe/fotos/logo_black.png" />
      <meta property="og:url" content="https://procu.com.pe" />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Procu - Encuentra Proveedores y Licitaciones" />
      <meta
        name="twitter:description"
        content="Optimiza tu búsqueda de proveedores y licitaciones con Procu. La mejor plataforma B2B en Perú."
      />
      <meta name="twitter:image" content="https://procu.com.pe/fotos/logo_black.png" />
    </Helmet>

    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <MsalProvider instance={msalInstance}>
        <UserProvider>
          <CompanyProvider>
            <Toaster />
            <Router>
              <Analytics />
              <ChatWidget />
              <Routes>
                {/* Páginas principales */}
                <Route path="/" element={<HomePage />} />
                <Route path="*" element={<NotFound />} />

                <Route
                  path="/menu/:userId/:nombreCompleto"
                  element={<Menu />}
                />

                {/* Catalogo */}
                <Route path="/catalogo" element={<Catalogo />} />

                {/* Usuarios */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgetpassword" element={<ForgetPassword />} />
                <Route path="/changepassword" element={<ChangePassword />} />

                <Route
                  path="/profile/:userId/:nombreCompleto"
                  element={<Profile />}
                />

                {/* Perfil y administración de empresa */}
                <Route
                  path="/company-profile/:companyId/:nombreEmpresa"
                  element={<CompanyProfile />}
                />
                <Route path="/company-register" element={<CompanyRegister />} />
                <Route
                  path="/company-profile/:companyId/admin"
                  element={<CompanyAdmin />}
                >
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="identificacion" element={<Identificacion />} />
                  <Route path="descripcion" element={<Descripcion />} />
                  <Route path="info-empresa" element={<InformacionEmpresa />} />
                  <Route path="contacto" element={<Contacto />} />
                  <Route path="servicios" element={<Servicios />} />
                  <Route path="usuarios" element={<CompanyUsers />} />
                  <Route path="zonas" element={<Zonas />} />
                </Route>

                {/* Licitaciones */}
                <Route path="/licitaciones" element={<Licitaciones />} />
                <Route path="/mis-licitaciones" element={<AdminLicitacion />} />
                <Route
                  path="/editar-licitacion/:tender_id"
                  element={<EditarLicitacion />}
                />
                <Route
                  path="/reusar-licitacion/:tender_id"
                  element={<ReusarLicitacion />}
                />
                <Route
                  path="/confirmar-edicion-licitacion/:tender_id"
                  element={<ConfirmarEdicion />}
                />
                <Route
                  path="/ver-postulaciones/:tender_id"
                  element={<Postulaciones />}
                />
                <Route
                  path="/tender/:tenderId/application/:applicationId"
                  element={<PostulacionCompleta />}
                />
                <Route
                  path="/ver-licitacion/:id/:titulo"
                  element={<LicitacionEspecifica />}
                />
                <Route
                  path="/admin-contrato/:contractId"
                  element={<GanadorLicitacion />}
                />
                {/* Nueva ruta para administrar postulaciones */}
                <Route
                  path="/mis-postulaciones"
                  element={<AdminPostulaciones />}
                />
                <Route
                  path="/detalle-postulacion/:applicationId"
                  element={<DetallePostulacion />}
                />

                {/* Crear licitaciones */}
                <Route
                  path="/create-tender-step-1"
                  element={<CreateTenderStep1 />}
                />
                <Route
                  path="/create-tender-step-2"
                  element={<CreateTenderStep2 />}
                />
                <Route
                  path="/create-tender-step-3"
                  element={<CreateTenderStep3 />}
                />
                <Route
                  path="/create-tender-step-4"
                  element={<CreateTenderStep4 />}
                />
                <Route path="/preview-tender" element={<PreviewTender />} />
                <Route
                  path="/licitaciones/:tenderId/comprobante-pago"
                  element={<SubirComprobante />}
                />

                {/* Nueva ruta para reseñas */}
                <Route path="/reseñas" element={<Reseñas />} />

                {/* Páginas legales */}
                <Route path="/condiciones-uso" element={<CondicionesUso />} />
                <Route path="/aviso-privacidad" element={<AvisoPrivacidad />} />

                {/* Precios y ayuda */}
                <Route path="/precios" element={<Pricing />} />
                <Route path="/como-funciona" element={<ComoFunciona />} />

                {/* Invitación a licitación */}
                <Route path="/invitacion/:token" element={<Invitacion />} />

                {/* Métricas internas de Procu */}
                <Route path="/staff/metricas" element={<StaffMetrics />} />
                <Route path="/staff/empresas" element={<StaffCompanyAdmin />} />
                <Route path="/staff/conversaciones" element={<StaffChatDashboard />} />
                <Route path="/staff/pagos-pendientes" element={<StaffPagosPendientes />} />
                <Route path="/staff/visitas" element={<StaffVisitas />} />

                {/* Favoritos */}
                <Route path="/favoritos" element={<Favoritos />} />
              </Routes>
            </Router>
          </CompanyProvider>
        </UserProvider>
      </MsalProvider>
    </GoogleOAuthProvider>
    </HelmetProvider>
  );
};

export default App;
